import * as Yup from "yup";
import { useState } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Autocomplete, Box, ListItemText, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { addDays, format } from "date-fns";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import Skeleton from "react-loading-skeleton";
import { useSubscription } from "store/auth/auth-store";
import { useTranslation } from "react-i18next";
import { useSOWsQuery } from "services/input-core/sows/get-all-sows";
import { useWorkersQuery } from "services/input-core/workers/get-all-workers";
import { useSendReports } from "services/input-core/reports/send-report";
import { SendReportPayload } from "services/input-core/reports/@types";
import { checkMessageErrors } from "utils/functions";
import { toast } from "react-toastify";
import { useReportsContext } from "store/reports/reportsContext";
import { ptBR } from "date-fns/locale";

interface ReportProps {
  isSow: boolean;
}

const Report: React.FC<ReportProps> = ({ isSow }) => {
  const { t } = useTranslation(["files-reports-messages", "list-errors"]);
  const { subscription } = useSubscription();
  const { setIsLoadingReport, socketNotification } = useReportsContext();
  const [initialDate, setInitialDate] = useState<Date>(new Date());
  const [fieldglassId, setFieldglassId] = useState(undefined);
  const [endDate, setEndDate] = useState<Date>(addDays(new Date(), 31));
  const [loading, setLoading] = useState(false);

  const { data: Sows, isLoading: isSowsLoading } = useSOWsQuery(
    subscription.subscriptionId,
    {
      enabled: subscription.subscriptionId && isSow,
    }
  );

  const { data: Workers, isLoading: isWorkersLoading } = useWorkersQuery(
    subscription.subscriptionId,
    {
      enabled: subscription.subscriptionId && !isSow,
    }
  );

  const { mutate: sendReport, isLoading: isLoadingReport } = useSendReports();

  const handleSendButton = async () => {
    try {
      const schema = Yup.object({
        endDate: Yup.string().required(t("end-date")),
        initialDate: Yup.string().required(t("start-date")),
        fieldglassId: Yup.string().required(t("select-fieldglassId")),
      });

      await schema.validate({
        fieldglassId: fieldglassId?.sowID,
        initialDate,
        endDate,
      });

      const params: SendReportPayload = {
        initialDate: format(initialDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        profile: isSow ? "SOW" : "WORKER",
        type: "csv",
        fieldglassId: isSow ? fieldglassId?.sowID : fieldglassId?.workerID
      };

      setIsLoadingReport(true);
      setLoading(true);

      sendReport(params, {
        onSuccess() {
          socketNotification();
          toast.info(t("report-processing"));
        },
        onError(error: any) {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.message,
                "WORKITEMS_NOT_FOUND"
              ),
              { ns: "list-errors" }
            )
          );
          setIsLoadingReport(false);
          setLoading(false);
        },
      });
    } catch (validationError) {
      toast.error(validationError.message);
      setLoading(false);
    }
  };

  return (
    <>
      <Box>
        <Box>
          {isSow ? (
            isSowsLoading ? (
              <Skeleton />
            ) : (
              <Autocomplete
                options={Sows || []}
                getOptionLabel={(option) =>
                  `${option.sowID} - ${option.sowName}` || ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={t("sow") as string}
                  />
                )}
                renderOption={(props, item) => (
                  <li {...props} key={item.sowID}>
                    <ListItemText>{`${item.sowID} - ${item.sowName}`}</ListItemText>
                  </li>
                )}
                value={fieldglassId}
                onChange={(_event, newValue) =>{
                  setFieldglassId(newValue)
                }}
              />
            )
          ) : isWorkersLoading ? (
            <Skeleton />
          ) : (
            <Autocomplete
              options={Workers || []}
              getOptionLabel={(option) =>
                `${option.workerID} - ${option.firstName} ${option.lastName}` ||
                ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t("worker") as string}
                />
              )}
              renderOption={(props, item) => (
                <li {...props} key={item.workerID}>
                  <ListItemText>{`${item.workerID} - ${item.firstName} ${item.lastName}`}</ListItemText>
                </li>
              )}
              value={fieldglassId}
              onChange={(_event, newValue) => {
                setFieldglassId(newValue)
              }}
            />
          )}

          <Box
            display="flex"
            sx={{
              border: "1px solid #ccc",
              padding: "20px",
              borderRadius: "5px",
              display: "flex",
              gap: "20px",
            }}
            justifyContent="space-around"
            marginY={5}
          >
            <LocalizationProvider
              adapterLocale={ptBR}
              dateAdapter={AdapterDateFns}
            >
              <MuiDatePicker
                label={t("start-date")}
                value={initialDate}
                onChange={(date) => {
                  setInitialDate(date);
                  setEndDate(undefined);
                }}
                disabled={loading}
                sx={{ marginRight: 5 }}
              />

              <MuiDatePicker
                label={t("end-date")}
                value={endDate}
                onChange={(date) => {
                  setEndDate(date);
                }}
                disabled={loading || !initialDate}
                minDate={initialDate}
                maxDate={addDays(initialDate, 31)}
              />
            </LocalizationProvider>
          </Box>

          <ButtonTS
            color="primary"
            onClick={handleSendButton}
            disabled={!fieldglassId || isLoadingReport}
          >
            {t("export")}
          </ButtonTS>
        </Box>
      </Box>
    </>
  );
};

export default Report;
